import type { TextSegmentParser } from '../types';
import type { TextDirection, BidiInstance } from './types';
import { isVisibleSegment } from './utils/isVisibleSegment';
import {
  REGULAR_SPACE,
  NON_BREAKING_SPACE,
  VARIATION_SELECTOR_16,
} from './utils/constants';
import { splitTextByLineBreaks } from './utils/text-splitting';

/**
  Character-level bidirectional text parsing algorithm based on Unicode Bidirectional Algorithm (UBA).
  
  PURPOSE:
  Simple character-level reordering for bidirectional text display. This approach applies
  the full Unicode Bidirectional Algorithm at the character level and segments the result
  into visual grapheme clusters for rendering.
  
  COMPONENTS:
  - "bidi-js": Implements Unicode Bidirectional Algorithm for complete text reordering
  - "Intl.Segmenter": Provides correct grapheme cluster segmentation for visual display
  
  WHY Intl.Segmenter (not str.split()):
  - Handles CJK (Chinese, Japanese, Korean) characters correctly
  - Supports complex emojis (👨‍👩‍👧‍👦) and emoji sequences with ZWJ
  - Processes ligatures (æ, œ) and accented characters (café, naïve)  
  - Correctly segments grapheme clusters (what users perceive as single characters)
  - Handles mixed languages and directions in one sentence
  - Preserves visual integrity of complex Unicode sequences
  
  ALGORITHM PHASES:
  
  1. TEXT PREPARATION::
     - Normalize space characters to non-breaking spaces (\u00A0)
     - Split text into lines based on line break characters
  
  2. For each line, apply Unicode Bidirectional Algorithm:
 
     Phase 1: BIDIRECTIONAL REORDERING
     - Apply bidi-js getEmbeddingLevels() to analyze character directionality
     - Use bidi-js getReorderedString() to get the complete reordered text
     - This handles all UBA complexity: embedding levels, runs, mirroring, etc.
    
     Phase 2: VARIATION SELECTOR FIXING
     - Fix orphaned variation selectors (VS-16: U+FE0F) that were separated during bidi reordering
     - Identify VS-16 characters not immediately preceded by compatible emoji
     - Attach orphaned VS-16 to the next suitable emoji that can take variation selectors
     - Uses Unicode property escapes (\p{Emoji}, \p{Emoji_Presentation}) for accurate detection
    
     Phase 3: GRAPHEME SEGMENTATION
     - Use Intl.Segmenter with 'grapheme' granularity on fixed text
     - Split into visual grapheme clusters (user-perceived characters)
     - Mark each segment as visible or invisible for rendering
  
  3. RESULT:
     - Return array of lines, each containing grapheme segments ready for rendering
  
  EXAMPLE - Mixed LTR/RTL:
  Input: "Hello שלום World"
  Phase 1: Space normalization → "Hello\u00A0שלום\u00A0World"
  Phase 2: Line splitting → ["Hello\u00A0שלום\u00A0World"] (single line)
  Phase 3: bidi-js reordering → "Hello\u00A0םולש\u00A0World" (Hebrew reversed)
  Phase 4: Variation selector fixing → (no VS-16 in this example)
  Phase 5: Grapheme segments → ["H", "e", "l", "l", "o", "\u00A0", "ם", "ו", "ל", "ש", "\u00A0", "W", "o", "r", "l", "d"]
  Result: Character-by-character segments with proper RTL text reordering
*/

export const parseGrapheme: TextSegmentParser = (
  str: string,
  textDirection: TextDirection,
  segmenter: Intl.Segmenter,
  bidi: BidiInstance,
) => {
  // replace spaces with non-breaking spaces
  const preparedString = str.replaceAll(REGULAR_SPACE, NON_BREAKING_SPACE);

  const lines = splitTextByLineBreaks(preparedString);

  const segmentsBrokenByLines = lines.map(line => {
    // Handle empty lines (consecutive line breaks)
    if (line.length === 0) {
      return [];
    }
    return reorderLine(line, bidi, textDirection, segmenter);
  });

  return {
    segmentsBrokenByLines,
  };
};

const reorderLine = (
  text: string,
  bidi: BidiInstance,
  textDirection: TextDirection,
  segmenter: Intl.Segmenter,
) => {
  // Phase 1: Bidirectional reordering
  const embeddingLevels = bidi.getEmbeddingLevels(text, textDirection);
  const preparedString = bidi.getReorderedString(text, embeddingLevels);

  // Phase 2: Fix orphaned variation selectors caused by a bug in bidi.js reordering
  const fixedString = fixOrphanedVariationSelectors(preparedString);

  // Phase 3: Grapheme segmentation
  const segments = Array.from(segmenter.segment(fixedString), segment => ({
    data: segment.segment,
    isVisible: isVisibleSegment(segment.segment),
  }));

  return segments;
};

/**
 * Fixes orphaned variation selectors (VS-16: U+FE0F) that were separated from their base characters
 * during bidirectional text reordering due to a bug in bidi.js.
 *
 * A VS-16 is considered "orphaned" if it's not immediately preceded by an emoji that can take VS-16.
 * This function finds orphaned VS-16 characters and attaches them to the next suitable emoji.
 *
 * VS-16 can become orphaned when bidi.js reorders the text because it doesn't consider the variation
 * selector as part of the emoji, so the order of emoji and VS-16 is flipped in the reordered text.
 *
 * @example
 * // Original text contains ♥️ which is actually ❤ (U+2764) + VS-16 (U+FE0F)
 * // Input: "♥️"  ->  ['\u2764', '\uFE0F']  (heart + variation selector)
 * //
 * // After bidi.js reordering (separates emoji components):
 * // Output: ['\uFE0F', '\u2764']  (VS-16 first, then heart - orphaned!)
 * //
 * // This function detects orphaned \uFE0F and reattaches it to \u2764:
 * // Result: ['\u2764, \uFE0F'] (properly combined emoji ♥️)
 */
const fixOrphanedVariationSelectors = (text: string): string => {
  const srcTextArr = [...text];

  // Identify orphaned VS-16 and track emojis that already have VS-16
  const orphanedVS16Indices: Array<number> = [];
  const emojiIndicesWithVS16 = new Set<number>();

  for (let i = 0; i < srcTextArr.length; i++) {
    if (srcTextArr[i] === VARIATION_SELECTOR_16) {
      const prevCharIdx = i - 1;
      // Check if VS-16 is properly paired by looking at the ORIGINAL source position
      const isPaired =
        prevCharIdx >= 0 && canTakeVariationSelector16(srcTextArr[prevCharIdx]);

      if (isPaired) {
        // Mark the emoji as having VS-16
        emojiIndicesWithVS16.add(prevCharIdx);
      } else {
        // This VS-16 is orphaned
        orphanedVS16Indices.push(i);
      }
    }
  }

  // Try to assign orphaned VS-16 to emojis that don't have VS-16 yet
  for (const orphanedVS16Index of orphanedVS16Indices) {
    let attached = false;

    // Look for emojis after this orphaned VS-16 that can take VS-16 and don't already have one
    for (
      let emojiIdx = orphanedVS16Index + 1;
      emojiIdx < srcTextArr.length;
      emojiIdx++
    ) {
      const char = srcTextArr[emojiIdx];

      // Check if this emoji can take VS-16 and doesn't already have one
      const canTakeVS16 = canTakeVariationSelector16(char);
      const alreadyHasVS16 =
        emojiIndicesWithVS16.has(emojiIdx) ||
        char.endsWith(VARIATION_SELECTOR_16);

      if (canTakeVS16 && !alreadyHasVS16) {
        // Found a suitable emoji, attach the orphaned VS-16 to it
        srcTextArr[emojiIdx] = char + VARIATION_SELECTOR_16;
        emojiIndicesWithVS16.add(emojiIdx);
        attached = true;
        break;
      }
    }

    if (attached) {
      // Mark this orphaned VS-16 as processed (will be removed from original position)
      srcTextArr[orphanedVS16Index] = '';
    }
  }

  // Filter out empty strings and join
  return srcTextArr.filter(char => char !== '').join('');
};

/**
 * Determines if a character can take Variation Selector-16 (U+FE0F) to display as emoji.
 * Uses Unicode property escapes for accurate detection.
 *
 * Characters that can take VS-16 are those that:
 * - Have the Emoji property (are emoji characters)
 * - But are NOT Emoji_Component characters (which are meant only for use in sequences)
 */
const canTakeVariationSelector16 = (text: string): boolean => {
  if (!text || text.length === 0) {
    return false;
  }

  try {
    const firstCodePoint = text.codePointAt(0);
    if (firstCodePoint === undefined) {
      return false;
    }

    const char = String.fromCodePoint(firstCodePoint);

    const hasEmoji = /\p{Emoji}/u.test(char);
    const isEmojiComponent = /\p{Emoji_Component}/u.test(char);

    return hasEmoji && !isEmojiComponent;
  } catch (e) {
    return false;
  }
};
